
import React, { useState } from 'react';
import { Tabs, Image, Spin } from 'antd';
import CommonModal from "PhishingManager/components/Modal";
import { ModalProps } from "PhishingManager/components/Modal/Modal.types";
import InteractionPreviewImg from "../images/interaction-preview.png";
import CalculationImg from "../images/calculation.png";
import EvaluationImg from "../images/evalution.png";
import ProcessImg from "../images/process-preview.png";
import DetectImg from "../images/detect.png";
import RatingDescImg from "../images/rating-desc.png";
import CheckListImg from "../images/checklist.png";
import { Icon } from 'Components';
import useDownloadPDFFromURL from 'Hooks/useDownloadFileFromURL';
import { AUTOMATION_PDF_URL, INTERACTION_PDF_URL } from '../CampaignReport.constant';

const LearnMoreModal: React.FC<ModalProps> = ({
    open,
    onClose,
    onConfirm
}) => {
    const [activeTab, setActiveTab] = useState("interaction");

    const { downloadFile, loading } = useDownloadPDFFromURL();

    return (
        <CommonModal
            visible={open}
            confirmText="Close"
            cancelText={null}
            iconType={null}
            onClose={onClose}
            onConfirm={onConfirm}
            modalTitle={"Learn More"}
            parentClass='learn-more-modal'>
            <Spin spinning={loading}>
                <div className='modal-body'>
                    <div className='tabs-section'>
                        <Tabs activeKey={activeTab} onChange={setActiveTab}>
                            <Tabs.TabPane tab="Interpreting Results" key="interaction">
                                <InterpretResult />
                            </Tabs.TabPane>

                            <Tabs.TabPane tab="Automation Detection" key="automation">
                                <AutomationDetect />
                            </Tabs.TabPane>
                        </Tabs>
                    </div>
                    <Icon type="download" className="icon-btn download-btn" onClick={() => downloadFile(activeTab === "interaction" ? INTERACTION_PDF_URL : AUTOMATION_PDF_URL, activeTab === "interaction" ? "interpreting_results" : "automation_detection")} />
                </div>
            </Spin>
        </CommonModal>
    );
};

export default LearnMoreModal;

const InterpretResult = () => {
    return <div className="content-section" id="interaction-content">
        <div>
            <p>Venza Phishing™ simulations test employees' security awareness by presenting real-world examples of email-based social engineering attacks.</p>
            <p>Over a chosen period, each campaign distributes simulated phishing emails to a group of employees. The emails link to a deceptive landing page that prompts recipients to enter sensitive information — gauging whether they recognise the threat.</p>
            <p>After the campaign concludes, results are compiled and distributed alongside recommendations for individual remediation and ongoing organisational training.</p>
        </div>

        <div>
            <h3>Understanding Interactions</h3>
            <p>Phishing simulations measure how users respond to risky data-security requests. There are four categories of user response:</p>
            <p>There are four categories of user responses to a Venza Phishing simulation:</p>

            <div className='result-desc'>
                <div className='passed'>
                    <span>PASSING</span>
                        <p><strong>No Interaction -</strong> the user does not open the email, visit the landing page, or submit any data.</p>
                        <p><strong>Opened Email -</strong> the user opens the email but does not click a link or submit data.</p>
                </div>

                <div className='failed'>
                    <span>Failing</span>
                        <p><strong>Clicked Link –</strong> the user opens the email and clicks an internal link but does not submit data on the landing page.</p>
                        <p><strong>Submitted Data –</strong> the user opens the email, clicks a link, and enters sensitive data on the landing page.</p>
                </div>
            </div>
            <Image preview={false} src={InteractionPreviewImg} alt="interaction-preview" />
        </div>

        <div>
            <h3>Reading the Summary</h3>
            <p>The Summary tab provides high-level results from the campaign, offering insights into your organisation's security posture and individual employee risk.</p>
            <ul>
                <li><strong>Executive Summary -</strong> an overview of organizational performance with aggregate metrics for key indicators.</li>
                <li><strong>Susceptible Employees -</strong> unique users who clicked a link or submitted data during the campaign.</li>
                <li><strong>All Results -</strong> the full set of results including both passing and failing users.</li>
            </ul>
            <p>The Pass Rate is the overall score — the percentage of users who responded appropriately. A higher rate indicates stronger data security. It is calculated as shown below:</p>
            <Image preview={false} src={CalculationImg} alt="calculation-preview" />
            <div className='note'>Note: The Email Opened count may be lower than Clicked Link due to email client limitations — tracking pixels do not load under all security settings. Multiple interactions by the same user are counted as one instance in headline metrics.</div>
        </div>

        <div>
            <h3>Automated Interaction Detection</h3>
            <p>Modern email systems use security tools — such as spam filters — that automatically interact with incoming email regardless of user behaviour. These “automated interactions” are present across all phishing platforms and most email environments.</p>
            <p className="mt-12">When undetected, they can decrease accuracy by — for example — incorrectly recording a security scanner's link-sniff as a user click.</p>
            <Image preview={false} src={ProcessImg} alt="process-preview" />
            <p>To counter this, Venza Phishing™ uses a proprietary Automated Interaction Detection (AID) algorithm that identifies software-based interactions and distinguishes them from real user actions.</p>
            <p>The AID algorithm evaluates Clicked Link and Submitted Data interactions only. It does not evaluate No Interaction or Opened Email.</p>
            <p>Details about algorithm criteria can be found on the Automation Checks tab of this report.</p>
            <p className="mt-12">The AID algorithm evaluates the Clicked Link and Submitted Data interactions. It does not evaluate the No Interaction and Email Opened interactions.</p>
            <Image preview={false} src={EvaluationImg} alt="evalution-preview" />
            <p>Using finely-tuned rules based on geolocation, timing, and interaction frequency, AID assigns each event a probability rating reflecting the likelihood it was automated rather than human. This rating appears in the “Automated Interaction” column of the Advanced tab.</p>
            <p>Because sourcing is inherently probabilistic, Venza recommends treating each potential interaction as an opportunity for ongoing awareness raising.</p>
        </div>

        <div>
            <h3>Property Level Results</h3>
            <p>The Property Level tab presents summary results segmented by property, enabling training managers to compare performance across their portfolio — identifying top performers and those who may benefit from additional training.</p>
            <p>Pass Rate, Emails Sent, Opened Email, Clicked Link, and Submitted Data are all reported per property using the same definitions as the Executive Summary.</p>
        </div>

        <div>
            <h3>Advanced Findings</h3>
            <p>The Advanced tab contains the complete data set collected during the campaign. For most clients, the Summary tab provides sufficient guidance — but this tab is available when a detailed per-user record is needed.</p>
            <p>Each row represents a unique interaction event and includes timestamps, IP address, internet service provider, and user agent (browser and OS). Unlike the Summary, multiple interactions by the same user appear as separate rows — so this tab may contain more rows than emails sent.</p>
            <p>Note: headline metrics in the Executive Summary consolidate multiple interactions per user into a single count.</p>
        </div>

        <div>
            <h3>Recommendations and Next Steps</h3>
            <p>Venza recommends assigning supplemental phishing training to all Susceptible Employees. A campaign-specific remediation course addresses the exact red flags users missed, alongside general email security best practices.</p>
            <p>For broader instruction, the Venza Learning™ Content Library includes additional courses — such as Phishing Defense — covering common hospitality email threats.</p>
            <p>Venza also offers microlessons, breakroom posters, and visual aids to strengthen email security organisation-wide. Contact your Customer Success Coach for more information.</p>
        </div>
    </div>
}

const AutomationDetect = () => {
    return <div className="content-section" id="automation-content">
        <div>
            <p>When analysing phishing simulation results, it is important to distinguish between genuine user activity and automated systems that interact with emails — such as security tools, mail gateways, and link-scanning bots.</p>
            <p>Automated activity does not reflect human behaviour and can distort performance metrics if left unfiltered. To address this, Venza uses a proprietary, weighted, dynamic algorithm to validate whether each interaction was performed by a real user or an automated system.</p>
            <p>Underwritten by advanced AI and machine learning, the Automated Interaction Detection (AID) Algorithm performs behavioural and technological profiling across more than 100 variables.</p>
        </div>

        <div>
            <h3>How It Works</h3>
            <p className="mt-12">AID categorises all campaign activity into two primary types:</p>
            <Image preview={false} src={DetectImg} alt="detect-preview" />
            <p>AID operates through a two-step workflow. It first evaluates whether the interaction can be confidently linked to a human user. If the evidence is insufficient, it then checks for signs of automation.</p>
            <p className="mt-12">At each step, AID applies a set of tests and assigns a confidence rating based on the predictive strength of the signals observed.</p>
            <Image preview={false} src={RatingDescImg} alt="rating-desc-preview" />
        </div>

        <div>
            <h3>Automation Checks</h3>
            <p className="mt-12">AID applies a broad set of evaluations to each interaction. Examples of the types of checks performed include:</p>
            <Image preview={false} src={CheckListImg} alt="checklist-preview" />
            <div className='note'>Because automation detection is inherently probabilistic, Venza recommends reviewing flagged interactions case-by-case and erring on the side of caution when assessing employee risk.</div>
        </div>
    </div>
}